import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { activeQuoteItems, getQuoteCommercialTotals, quoteLineBase } from "./quoteLineMath";
import { formatInr } from "./formatInr";

const A4_WIDTH = 210;
const A4_HEIGHT = 297;
const MARGIN_X = 18; // Clean, standard, professional A4 margin
const CONTENT_WIDTH = A4_WIDTH - MARGIN_X * 2; // 174 mm
const FOOTER_Y = 280;

// User requested brand color #2C1943 (rgb: 44, 25, 67)
const BRAND_PLUM = [44, 25, 67]; // #2C1943 - Primary dark plum brand color
const BRAND_PURPLE = [44, 25, 67]; // #2C1943 - Accent color for bullets & labels
const BRAND_DEEP_TEAL = [44, 25, 67]; // Alias for compatibility
const BODY_TEXT = [30, 35, 45]; // #1E232D - Crisp dark charcoal body text
const MUTED_TEXT = [90, 100, 115]; // Muted grey text
const LINE_GREY = [205, 212, 224]; // Divider border color
const LINK_BLUE = [28, 85, 210]; // Clickable links
const BODY_SIZE = 9.8;
const BODY_LINE_HEIGHT = 5.2;

const ASSETS = {
  montserratRegular: "/fonts/Montserrat-Regular.ttf",
  montserratBold: "/fonts/Montserrat-Variable.ttf",
};

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return window.btoa(binary);
};

const loadFontAsBase64 = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Could not load PDF font: ${url}`);
    return arrayBufferToBase64(await res.arrayBuffer());
  } catch (e) {
    console.warn("Font loading fallback:", e);
    return null;
  }
};

const registerFonts = (doc, montserratRegular, montserratBold) => {
  if (montserratRegular) {
    doc.addFileToVFS("Montserrat-Regular.ttf", montserratRegular);
    doc.addFont("Montserrat-Regular.ttf", "Montserrat", "normal");
  }
  if (montserratBold) {
    doc.addFileToVFS("Montserrat-Bold.ttf", montserratBold);
    doc.addFont("Montserrat-Bold.ttf", "Montserrat", "bold");
  } else if (montserratRegular) {
    doc.addFont("Montserrat-Regular.ttf", "Montserrat", "bold");
  }
};

const safeText = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
};

const formatAgreementDate = (value = new Date()) => {
  const date = new Date(value);
  const validDate = Number.isNaN(date.getTime()) ? new Date() : date;
  return validDate.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
};

const loadImageAsBase64 = (url, format = "jpeg") =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const mime = format === "png" ? "image/png" : "image/jpeg";
      const quality = format === "png" ? undefined : 0.92;
      resolve(canvas.toDataURL(mime, quality));
    };
    img.onerror = () => resolve("");
    img.src = url;
  });

const trimImageDataUrl = (dataUrl, { threshold = 245 } = {}) =>
  new Promise((resolve) => {
    if (!dataUrl) {
      resolve("");
      return;
    }
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);

      let minX = width;
      let minY = height;
      let maxX = 0;
      let maxY = 0;

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const i = (y * width + x) * 4;
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          const isEmpty = a < 12 || (r > threshold && g > threshold && b > threshold);
          if (!isEmpty) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

      if (maxX <= minX || maxY <= minY) {
        resolve(dataUrl);
        return;
      }

      const pad = 4;
      const cropX = Math.max(0, minX - pad);
      const cropY = Math.max(0, minY - pad);
      const cropW = Math.min(width - cropX, maxX - minX + 1 + pad * 2);
      const cropH = Math.min(height - cropY, maxY - minY + 1 + pad * 2);
      const trimmed = document.createElement("canvas");
      trimmed.width = cropW;
      trimmed.height = cropH;
      trimmed.getContext("2d").drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      resolve(trimmed.toDataURL("image/png"));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

const getImageDimensions = (dataUrl) =>
  new Promise((resolve) => {
    if (!dataUrl) {
      resolve({ width: 0, height: 0 });
      return;
    }
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = dataUrl;
  });

const fitImageBoxMm = (imgW, imgH, maxW, maxH) => {
  if (!imgW || !imgH) return { width: maxW, height: maxH };
  const ratio = imgW / imgH;
  let width = maxW;
  let height = width / ratio;
  if (height > maxH) {
    height = maxH;
    width = height * ratio;
  }
  return { width, height };
};

const imageFormatFromDataUrl = (dataUrl) =>
  String(dataUrl).startsWith("data:image/png") ? "PNG" : "JPEG";

const getClientName = (project = {}, quote = {}) =>
  safeText(project?.client?.name || project?.clientName || quote?.client?.name, "Client");

const getClientEmail = (project = {}, quote = {}) =>
  safeText(project?.client?.email || quote?.client?.email || project?.email || quote?.email, "");

const getClientPhone = (project = {}, quote = {}) =>
  safeText(project?.client?.phone || project?.client?.mobile || quote?.client?.phone || quote?.client?.mobile || project?.phone || quote?.phone, "");

const getProjectAddress = (project = {}) => {
  const client = typeof project?.client === "object" ? project.client : {};
  const address = [
    project?.propertyAddress || client?.propertyAddress || client?.address?.street || client?.address?.line1,
    project?.city || client?.address?.city,
    project?.state || client?.address?.state,
    client?.address?.pincode,
  ].filter(Boolean);
  return safeText(address.join(", "), "Project address not available");
};

const addBackground = (doc, bgImage) => {
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, A4_WIDTH, A4_HEIGHT, "F");
  if (bgImage) {
    doc.addImage(bgImage, "JPEG", 0, 0, A4_WIDTH, A4_HEIGHT);
  }
};

const addFooter = (doc, pageNumber, totalPages) => {
  doc.setDrawColor(...LINE_GREY);
  doc.setLineWidth(0.35);
  doc.line(MARGIN_X, FOOTER_Y - 5, A4_WIDTH - MARGIN_X, FOOTER_Y - 5);

  doc.setFont("Montserrat", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...LINK_BLUE);
  doc.textWithLink("www.tatsavispaces.com", MARGIN_X, FOOTER_Y + 1, { url: "https://www.tatsavispaces.com" });

  doc.setTextColor(...MUTED_TEXT);
  doc.text(totalPages ? `Page ${pageNumber} of ${totalPages}` : `${pageNumber}`, MARGIN_X, FOOTER_Y + 7);

  doc.setTextColor(...BODY_TEXT);
  doc.text("Microinch Hub Solutions LLP", A4_WIDTH - MARGIN_X, FOOTER_Y + 1, { align: "right" });
  doc.setTextColor(...MUTED_TEXT);
  doc.text("A-210, KPCT Mall, Fatima Nagar, Pune-13", A4_WIDTH - MARGIN_X, FOOTER_Y + 7, { align: "right" });
};

const ensureRoom = (doc, bgImage, y, needed = 16) => {
  if (y + needed <= FOOTER_Y - 10) return y;
  doc.addPage();
  addBackground(doc, bgImage);
  return 22; // clean top margin on subsequent pages
};

const drawCenteredTopLogo = async (doc, logoImage) => {
  if (!logoImage) return 36;
  const dims = await getImageDimensions(logoImage);
  const { width, height } = fitImageBoxMm(dims.width, dims.height, 58, 16);
  const x = (A4_WIDTH - width) / 2;
  const y = 14;
  doc.addImage(logoImage, imageFormatFromDataUrl(logoImage), x, y, width, height);
  return y + height + 13; // Generous space (13mm gap) between logo and "CUSTOMER AGREEMENT CONTRACT"
};

const buildQuoteTableRows = (quote) => {
  const items = activeQuoteItems(quote?.items || []);
  if (!items.length) return [];

  const groups = [];
  const byName = new Map();

  items.forEach((item) => {
    const spaceName = safeText(
      item?.space || item?.section || item?.Space || item?.room || item?.area || item?.defaultSpace,
      "General Scope"
    );
    if (!byName.has(spaceName)) {
      const group = { name: spaceName, items: [], total: 0 };
      byName.set(spaceName, group);
      groups.push(group);
    }
    const group = byName.get(spaceName);
    const amount = quoteLineBase(item);
    group.items.push({ item, amount });
    group.total += amount;
  });

  const rows = [];
  let serial = 1;

  groups.forEach((group) => {
    // Space header row (centered, bold, same color as subheadings)
    rows.push([
      {
        content: group.name.toUpperCase(),
        colSpan: 5,
        isSpaceHeader: true,
        styles: {
          fillColor: [244, 240, 248],
          textColor: BRAND_PLUM,
          fontStyle: "bold",
          fontSize: 9,
          halign: "center",
          cellPadding: { top: 2.4, bottom: 2.4, left: 3, right: 3 },
        },
      },
    ]);

    group.items.forEach(({ item, amount }) => {
      const itemName = safeText(item?.itemName || item?.name || item?.item || item?.title, "-");
      const desc = safeText(item?.lineDescription || item?.itemDetails || item?.description, "");
      const fullItemText = desc && desc !== "-" && desc !== itemName ? `${itemName}\n${desc}` : itemName;

      const material = safeText(
        item?.category || item?.quoteMaterial || item?.material || item?.spec,
        "-"
      );

      let dimension = "-";
      const length = Number(item?.length);
      const height = Number(item?.height);
      const qty = Number(item?.qty);
      if (Number.isFinite(length) && length > 0 && Number.isFinite(height) && height > 0) {
        dimension = `${length.toLocaleString("en-IN", { maximumFractionDigits: 2 })} x ${height.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
      } else if (Number.isFinite(qty) && qty > 0) {
        dimension = `${qty} Qty`;
      }

      rows.push([
        serial++,
        fullItemText,
        material,
        dimension,
        formatInr(amount),
      ]);
    });

    // Space subtotal row (rich bold with BRAND_PLUM color)
    rows.push([
      {
        content: `${group.name} Subtotal:`,
        colSpan: 4,
        isSubtotalLabel: true,
        styles: {
          halign: "right",
          fontStyle: "bold",
          fontSize: 8.5,
          textColor: BRAND_PLUM,
          fillColor: [248, 244, 252],
          cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
        },
      },
      {
        content: `Rs. ${formatInr(group.total)}/-`,
        isSubtotalAmount: true,
        styles: {
          halign: "right",
          fontStyle: "bold",
          fontSize: 8.5,
          textColor: BRAND_PLUM,
          fillColor: [248, 244, 252],
          cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
        },
      },
    ]);
  });

  return rows;
};

const drawRunsLine = (doc, bgImage, runs, x, y, options = {}) => {
  const fontSize = options.fontSize || BODY_SIZE;
  const lineHeight = options.lineHeight || BODY_LINE_HEIGHT;
  const maxWidth = options.maxWidth || CONTENT_WIDTH;
  const tokens = [];

  runs.forEach((run) => {
    String(run.text || "")
      .split(/(\s+)/)
      .filter(Boolean)
      .forEach((text) => tokens.push({ text, bold: Boolean(run.bold), color: run.color }));
  });

  let line = [];
  let lineWidth = 0;
  const flush = () => {
    if (!line.length) return;
    y = ensureRoom(doc, bgImage, y, lineHeight + 2);
    let cursorX = x;
    line.forEach((token) => {
      doc.setFont("Montserrat", token.bold ? "bold" : "normal");
      doc.setFontSize(fontSize);
      doc.setTextColor(...(token.color || options.color || (token.bold ? BRAND_PLUM : BODY_TEXT)));
      doc.text(token.text, cursorX, y);
      if (token.bold) {
        doc.text(token.text, cursorX + 0.16, y);
        doc.text(token.text, cursorX + 0.08, y + 0.06);
        doc.text(token.text, cursorX + 0.08, y - 0.06);
      }
      cursorX += doc.getTextWidth(token.text);
    });
    y += lineHeight;
    line = [];
    lineWidth = 0;
  };

  tokens.forEach((token) => {
    doc.setFont("Montserrat", token.bold ? "bold" : "normal");
    doc.setFontSize(fontSize);
    const tokenWidth = doc.getTextWidth(token.text);
    if (line.length && lineWidth + tokenWidth > maxWidth) {
      flush();
      if (/^\s+$/.test(token.text)) return;
    }
    line.push(token);
    lineWidth += tokenWidth;
  });
  flush();
  return y;
};

const drawParagraph = (doc, bgImage, text, y, options = {}) => {
  const fontSize = options.fontSize || BODY_SIZE;
  const lineHeight = options.lineHeight || BODY_LINE_HEIGHT;
  const maxWidth = options.maxWidth || CONTENT_WIDTH;
  const x = options.x || MARGIN_X;
  const isBold = Boolean(options.bold);
  doc.setFont("Montserrat", isBold ? "bold" : "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(...(options.color || (isBold ? BRAND_PLUM : BODY_TEXT)));
  const lines = doc.splitTextToSize(String(text || ""), maxWidth);
  lines.forEach((line) => {
    y = ensureRoom(doc, bgImage, y, lineHeight + 2);
    doc.text(line, x, y);
    if (isBold) {
      doc.text(line, x + 0.16, y);
      doc.text(line, x + 0.08, y + 0.06);
      doc.text(line, x + 0.08, y - 0.06);
    }
    y += lineHeight;
  });
  return y;
};

const drawSectionTitle = (doc, bgImage, number, title, y) => {
  y = ensureRoom(doc, bgImage, y + 4.5, 20);
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(11.2);
  doc.setTextColor(...BRAND_PLUM);
  const text = `${number}. ${title}`;
  doc.text(text, MARGIN_X, y);
  doc.text(text, MARGIN_X + 0.16, y);
  doc.text(text, MARGIN_X + 0.08, y + 0.06);
  doc.text(text, MARGIN_X + 0.08, y - 0.06);
  return y + 5.2;
};

const drawDivider = (doc, bgImage, y) => {
  y = ensureRoom(doc, bgImage, y, 8);
  doc.setDrawColor(...LINE_GREY);
  doc.setLineWidth(0.3);
  doc.line(MARGIN_X, y, A4_WIDTH - MARGIN_X, y);
  return y + 5;
};

const drawBullets = (doc, bgImage, bullets, y, options = {}) => {
  const indent = options.indent || 0;
  const isBold = Boolean(options.bold);
  bullets.forEach((bullet) => {
    const text = String(bullet || "");
    y = ensureRoom(doc, bgImage, y, 10);
    doc.setFont("Montserrat", isBold ? "bold" : "normal");
    doc.setFontSize(BODY_SIZE);
    doc.setTextColor(...(isBold ? BRAND_PLUM : BRAND_PURPLE));
    doc.text("•", MARGIN_X + 1 + indent, y);
    doc.text("•", MARGIN_X + 1.15 + indent, y);
    y = drawParagraph(doc, bgImage, text, y, {
      x: MARGIN_X + 6 + indent,
      maxWidth: CONTENT_WIDTH - 6 - indent,
      bold: isBold,
      color: isBold ? BRAND_PLUM : BODY_TEXT,
    });
    y += 1.0;
  });
  return y;
};

const drawLabeledBullets = (doc, bgImage, bullets, y, options = {}) => {
  const indent = options.indent || 0;
  bullets.forEach(({ label, text }) => {
    y = ensureRoom(doc, bgImage, y, 10);
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(BODY_SIZE);
    doc.setTextColor(...BRAND_PURPLE);
    doc.text("•", MARGIN_X + 1 + indent, y);
    doc.text("•", MARGIN_X + 1.15 + indent, y);
    y = drawRunsLine(
      doc,
      bgImage,
      [
        { text: `${label}: `, bold: true, color: BRAND_PLUM },
        { text: String(text || ""), color: BODY_TEXT },
      ],
      MARGIN_X + 6 + indent,
      y,
      { maxWidth: CONTENT_WIDTH - 6 - indent }
    );
    y += 1.0;
  });
  return y;
};

const drawNumberedItem = (doc, bgImage, numberStr, text, y, options = {}) => {
  const indent = options.indent || 0;
  y = ensureRoom(doc, bgImage, y, 10);
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(BODY_SIZE);
  doc.setTextColor(...BRAND_PLUM);
  doc.text(`${numberStr}.`, MARGIN_X + 2 + indent, y);
  doc.text(`${numberStr}.`, MARGIN_X + 2.15 + indent, y);
  y = drawParagraph(doc, bgImage, text, y, {
    x: MARGIN_X + 8 + indent,
    maxWidth: CONTENT_WIDTH - 8 - indent,
  });
  y += 1.0;
  return y;
};

const drawSubheading = (doc, bgImage, text, y) => {
  y = ensureRoom(doc, bgImage, y + 3.5, 16);
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(10.2);
  doc.setTextColor(...BRAND_PLUM);
  doc.text(text, MARGIN_X, y);
  doc.text(text, MARGIN_X + 0.14, y);
  doc.text(text, MARGIN_X + 0.07, y + 0.06);
  doc.text(text, MARGIN_X + 0.07, y - 0.06);
  return y + 4.8;
};

const drawQuoteLink = (doc, bgImage, quotePdfUrl, y) => {
  const url = safeText(quotePdfUrl);
  if (!url) return y;
  y = ensureRoom(doc, bgImage, y, 8);
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(BODY_SIZE);
  doc.setTextColor(...BRAND_PLUM);
  doc.text("Quotation Reference: ", MARGIN_X, y);
  doc.text("Quotation Reference: ", MARGIN_X + 0.16, y);
  doc.text("Quotation Reference: ", MARGIN_X + 0.08, y + 0.06);
  doc.text("Quotation Reference: ", MARGIN_X + 0.08, y - 0.06);
  const labelW = doc.getTextWidth("Quotation Reference: ");
  doc.setFont("Montserrat", "normal");
  doc.setTextColor(...LINK_BLUE);
  doc.textWithLink("Click here to view detailed quotation scope document", MARGIN_X + labelW, y, { url });
  return y + 5.5;
};

export const DEFAULT_AGREEMENT_TERMS = {
  paymentTerms: `The payment for the project shall be made in the following milestones:

Payment Schedule:
• 10% – Design Initiation: Payable in advance to commence 2D drawings and 3D design work.
• 50% – Execution Commencement: Payable after design finalisation and before commencement of on-site execution, including carpenter, POP and electrical advances, and procurement of plywood, inner laminates and adhesives.
• 35% – Basic Structure Completion: Payable immediately upon completion of the basic carpentry structure and before ordering/procuring outer laminates, hardware, accessories (including mirrors, locks and handles), and arranging the corresponding labour and installation work.
• Final 5% Payment, Inspection & Handover:
  • Tatsavi Spaces shall conduct a pre-final inspection approximately 2 days before the scheduled handover to identify any pending items within the approved scope.
  • Any minor pending work, finishing, alignment or adjustment identified during the inspection shall be completed within the following 48 hours, wherever practically possible.
  • The final 5% payment shall be due and payable on the scheduled handover date, following completion of the agreed scope and final inspection.
  • Minor pending works that do not materially affect the use or functionality of the completed work shall not be a reason to withhold, delay or deduct the final 5% payment.
  • Upon receipt of the complete final payment, Tatsavi Spaces shall complete final cleaning, washroom deep cleaning and removal of execution-related scrap/waste within 48 hours, subject to site accessibility, followed by final handover.
  • Any work or requirement outside the approved scope shall be charged separately and shall not affect the final payment.
  • Any delay in payment may result in suspension of cleaning, handover and related services until the outstanding amount is cleared.`,

  scopeChanges: `• Any work not mentioned in the quotation or scope document will be considered as additional work.
• Such work will be charged separately and will only commence after mutual consent and written approval.
• Requests like “do this little change” will not be entertained without checking feasibility and cost.`,

  projectTimeline: `• The tentative timeline for project completion will be 60 to 90 Days from the date of finalizing 3d Designs.
• The timeline is subject to changes due to external factors like material unavailability, client-side delays, natural calamities, or payment delays.
• In case of delays from the client (decision approvals, payments, access to site), the delivery timeline will be adjusted accordingly.
• Any society/building restrictions, including limited working hours, restrictions on noisy work, restricted entry or exit of workers/materials, non-permission for labourers to stay at the site, or any other society-related limitations, may affect the project schedule. In such cases, the project timeline shall be extended accordingly and Tatsavi Spaces shall not be responsible for delays arising from such restrictions.
• Any third-party involvement, including personal carpenters, electricians, plumbers, contractors, vendors or other workers engaged by the Client, without prior approval from Tatsavi Spaces, may affect the quality, coordination and project timeline.`,

  siteAccess: `• The Client shall provide full and uninterrupted access to the site during the agreed working hours to enable Tatsavi Spaces and its authorised workers to carry out the work.
• Any society/building restrictions, including limited working hours, restrictions on noisy work, restricted entry or exit of workers/materials, non-permission for labourers to stay at the site, or any other society-related limitations, may affect the project schedule. In such cases, the project timeline shall be extended accordingly and Tatsavi Spaces shall not be responsible for delays arising from such restrictions.
• Any third-party involvement, including personal carpenters, electricians, plumbers, contractors, vendors or other workers engaged by the Client, without prior approval from Tatsavi Spaces, may affect the quality, coordination and project timeline.
• Tatsavi Spaces shall not be responsible for any delay, damage, quality issue, rework or coordination issue arising from the involvement or work of any third party appointed by the Client.`,

  materialSelection: `• Material selection will be done with the client from trusted vendors/brands as mutually agreed.
• The Company is committed to delivering what is approved, and variations (if any) will be informed in advance.`,

  cancellationRefunds: `• In case the client cancels the project post finalization there will be no refund policy.`,

  handoverCleaning: `• Upon receipt of the complete final payment, Tatsavi Spaces will carry out the final cleaning and removal of execution-related waste/trash within 48 hours*, subject to site accessibility.`,
  handoverApproval: `• Upon receipt of the complete final payment, Tatsavi Spaces will carry out the final cleaning and removal of execution-related waste/trash within 48 hours*, subject to site accessibility.`,

  disputeResolution: `• Any disputes arising shall be first attempted to be resolved amicably.
• In case of legal matters, the jurisdiction will lie in Pune, Maharashtra.`,

  declaration: `By signing this document, both parties agree to all the terms and conditions mentioned above. The attached quotation will be treated as part of this agreement.`,

  warrantySupport: `[1. 30-Day Complimentary Support]
• Tatsavi Spaces provides 30 days of complimentary after-sales support from the date of final handover.
• During this 30-day period, issues related to the workmanship or installation of the existing approved scope, including minor alignment adjustments, loose edge binding, laminate bubbles and minor installation-related issues, will be attended to without labour, service or travel charges.
• This complimentary support does not cover any new work, modifications, additions, damage due to misuse, negligence, external factors or work altered by any third party.

[2. Support After 30 Days]
• After completion of the 30-day complimentary support period, service requests related to alignment, loose edge binding, laminate bubbles, minor adjustments, hardware servicing or site visits will be chargeable.
• Applicable service, labour and travel charges may be levied depending on the nature of the service required.
• Any new work, modification, alteration or addition requested by the client will be quoted and charged separately.

[3. Ply Warranty]
• Tatsavi Spaces provides a 10-year warranty on the ply supplied and used by Tatsavi Spaces in the project, subject to applicable warranty conditions.
• The warranty covers manufacturing and material defects only and excludes damage arising from misuse, excessive moisture or water exposure, termites, external impact, unauthorised modifications, improper maintenance, or normal wear and tear.

[4. Hardware Warranty]
• Hinges supplied and installed by Tatsavi Spaces are covered under a 2-year replacement warranty, subject to the applicable manufacturer's warranty terms.
• If a hinge is found to be defective due to a manufacturing fault within the warranty period, the replacement hardware will be provided free of cost.
• Rusting, corrosion, oxidation, or deterioration of the hardware due to moisture, water exposure, humidity, condensation, chemical exposure, or environmental conditions is not covered under warranty, irrespective of when it occurs during the warranty period.
• Service visits, technician charges or travel charges after the 30-day complimentary support period may be applicable, even where the replacement hardware itself is covered under warranty.
• Warranty does not cover damage caused by misuse, physical impact, unauthorised modification, improper handling, negligence, rusting/corrosion, or normal wear and tear.

[5. Warranty & Service Conditions]
• Warranty applies only to products and workmanship supplied, fabricated, assembled or installed by Tatsavi Spaces as part of the approved project scope.
• Warranty does not apply to products supplied by the client or any work, alteration or modification carried out by third parties.
• All warranty claims are subject to inspection and verification by Tatsavi Spaces and/or the respective manufacturer.
• Warranty coverage is limited to the repair or replacement of the affected component, as applicable, and does not include compensation for any consequential, indirect or incidental loss or damage.

[Warranty Coverage]
Tatsavi Spaces provides a 10-year warranty on the ply material supplied and used by Tatsavi Spaces within the approved project scope. The carpentry work is fabricated, assembled and installed at the project site in accordance with the approved design and specifications.

[1. Ply Material Warranty]
The 10-year warranty covers significant manufacturing or material defects in the ply that materially affect its normal intended structural or functional use, including:
• Abnormal Bulging or Swelling – Unusual swelling, expansion or deformation of the ply under normal indoor conditions.
• Layer Separation / Delamination – Separation of ply layers attributable to a material or manufacturing defect under normal usage conditions.
• Structural Deterioration – Significant deterioration or failure of the ply that is attributable to a material or manufacturing defect and not to external or site-related factors.
• Material Defects – Significant defects in the supplied ply that materially affect its intended structural or functional performance.
If a covered ply defect is identified, Tatsavi Spaces will inspect the affected area and, where the issue is determined to be covered under this warranty, provide repair or replacement of the affected ply component, at Tatsavi Spaces' discretion and subject to practical feasibility.

[2. Site Carpentry & Workmanship Support]
As the carpentry is fabricated, assembled and installed at the project site, Tatsavi Spaces provides 30 days of complimentary post-handover support for genuine workmanship and installation-related issues arising within the approved project scope.
The complimentary support may include:
• Minor alignment, levelling and adjustment issues
• Minor fitting or installation issues
• Minor installation-related finishing corrections
• Hardware fitting, alignment or adjustment issues
Any issue reported during the 30-day complimentary support period will be inspected and assessed by Tatsavi Spaces. Where the issue is determined to be attributable to Tatsavi Spaces' workmanship or installation and falls within the approved project scope, it will be attended to without labour, service or travel charges.
The complimentary support does not cover new requirements, additions, alterations, modifications, damage caused by misuse, negligence, water or moisture exposure, external damage, third-party intervention, or issues arising from conditions beyond Tatsavi Spaces' control.
The 30-day complimentary support period begins from the date of final handover of the project and does not extend or restart due to any subsequent service visit or rectification.

[3. Service After the 30-Day Complimentary Period]
After expiry of the 30-day complimentary support period, any service, inspection, adjustment, rectification or site visit requested by the client will be subject to applicable labour, service and travel charges, unless the issue is expressly covered under a separate applicable product warranty.
This may include, but is not limited to:
• Alignment, levelling or adjustment
• Loose or damaged edge binding
• Laminate bubbling, lifting or related surface issues
• Minor finishing corrections
• Hardware servicing or adjustment
• Site inspection or service visits
Any such issue will first be inspected and assessed by Tatsavi Spaces, and the applicable charges, where required, will be communicated before the service is undertaken.
Product-specific warranties, including warranties for ply and hardware, shall remain governed by their respective warranty terms and conditions and shall not be interpreted as extending the 30-day complimentary workmanship support period.
Any repair, replacement or service carried out after the complimentary period does not create or extend any additional warranty beyond the original applicable warranty period.

[4. Warranty Exclusions]
The ply warranty applies only to defects attributable to the material or manufacturing quality of the ply. It does not cover any damage, deterioration, deformation or failure caused directly or indirectly by:
• Water leakage, seepage, flooding or excessive moisture exposure
• Damp walls, condensation or prolonged exposure to humidity or moisture
• Termite, pest, fungal or other biological damage
• Physical impact, misuse, negligence, improper handling or excessive force
• Normal wear and tear, ageing or deterioration arising from regular use
• Unauthorised alterations, modifications, repairs, dismantling or reinstallation
• Any work, alteration, repair or modification carried out by the client or any third party
• Structural movement, settlement, cracks or other civil, architectural or site-related conditions
• Plumbing, electrical, appliance or drainage-related leakage or damage
• Improper cleaning, maintenance, storage or use of the furniture
• Any new addition, modification, alteration or relocation requested or carried out by the client after completion
• Damage resulting from site conditions or circumstances beyond the reasonable control of Tatsavi Spaces
• Any defect or damage that has been aggravated due to failure to promptly report the issue or provide reasonable access for inspection and rectification
For avoidance of doubt, any damage arising from the above conditions shall not be treated as a manufacturing or material defect in the ply and shall not be covered under the 10-year ply warranty.

[5. Warranty Assessment]
All warranty claims are subject to inspection by Tatsavi Spaces. The cause of the issue will be assessed to determine whether it falls under the applicable warranty or service support.
The warranty applies only to ply material supplied and installed by Tatsavi Spaces as part of the approved project scope and does not apply to materials supplied by the client or third parties.

[Warranty Limitations & Exclusions]
This warranty does not cover natural characteristics of wood, normal usage-related changes, or damages caused by external factors. Exclusions include but are not limited to:

[1. Natural Wood Characteristics]
• Wood Traits: Variations in grain, colour, mineral streaks, or knots are inherent to natural wood and highlight its uniqueness.
• Seasonal Wood Movement: Expansion and contraction due to humidity may cause hairline cracks or minor panel shifts. These are natural behaviours of wood and not defects. To minimise, maintain indoor humidity between 40–50%.
• Colour Changes Over Time: Sunlight, smoke, cleaning agents, and environmental exposure may gradually alter wood colour/texture. Such changes are expected and not covered.

[2. Moisture & Water Damage]
Damage caused by leaks, seepage, high humidity, or prolonged water contact—including negligence like unaddressed spills or wet conditions—will not be covered.

[3. Rust or Corrosion]
Hardware or metal parts damaged due to moisture, harsh chemicals, or lack of maintenance are not covered.

[4. Incorrect Use or Unauthorised Modifications]
• Alterations, repairs, or modifications carried out by unauthorised personnel.
• Misuse of cabinetry for purposes other than intended.
• Damage from abrasive/corrosive cleaning agents or unapproved chemicals.

[6. External Installations & Modifications]
• Non-Tatsavi Accessories: Issues arising from third-party products (e.g., countertops, appliances, lighting, plumbing) are not covered.
• Post-Installation Adjustments: Cabinet relocation, dismantling, or structural modifications after installation void the warranty.

[7. Outdoor & Weather Exposure]
• Exterior Woodwork: Installations directly exposed to sunlight, rain, or outdoor weather conditions.
• Stone Countertops: Natural stones like granite or marble used for countertops are not included under this warranty.

[8. Uncontrollable or External Factors]
• Cosmetic damage (minor scratches, dents, cuts) from day-to-day use.
• Damages caused by accidents, fire, floods, extreme weather, improper storage, or vandalism.

[9. Normal Wear & Tear]
• Cushion/foam softening or flattening with regular use.
• Fading/discolouration of finishes or upholstery due to sunlight exposure.
• Cuts, scratches, dents, or abrasions from daily wear or accidental impacts.

For more details you can visit www.tatsavispaces.com/terms-and-conditions`,

  termsConditions: `• Quotation Scope & Exclusions: The quotation includes only the items, materials, services and work specifically mentioned in the quotation. Any item or work not expressly mentioned shall be treated as excluded, including granite tops, plumbing, civil work, structural modifications and other allied works, unless specifically included in writing by Tatsavi Spaces.
• Civil & Third-Party Work: Tatsavi Spaces provides no warranty, guarantee or service responsibility for any civil work, plumbing, electrical work, structural work or any other work that is not directly executed by Tatsavi Spaces. Any such work shall remain outside the warranty and service obligations of Tatsavi Spaces.
• Recommended / Third-Party Vendors: Tatsavi Spaces may recommend or introduce vendors for works outside its approved scope as a convenience to the client. Such recommendation or introduction does not constitute an undertaking, guarantee, supervision responsibility or warranty by Tatsavi Spaces. The client shall be solely responsible for the vendor's appointment, scope, pricing, payment, workmanship, quality, safety, timelines and performance. Any dispute, defect, delay, damage or loss arising from such third-party work shall be resolved directly between the client and the concerned vendor, and Tatsavi Spaces shall have no liability in respect thereof.
• Transport & Assembly: Transport and assembly are included within Pune city limits for the approved project scope. Additional transportation, loading, unloading or related charges may apply for locations outside Pune.
• Non-Refundable Payments: All payments made to Tatsavi Spaces, including the booking/advance amount, are non-refundable, except where otherwise expressly agreed in writing by Tatsavi Spaces.
• Measurement Tolerance: All dimensions, quantities and areas mentioned in the quotation are approximate and subject to a ±10% variation based on final site measurements, approved drawings and 3D design finalisation. Any resulting change in quantity, material or price shall be adjusted accordingly.
• Quotation Validity: The quoted price is tentative until final design, site measurements, material specifications and scope are finalised and approved. The final price may vary based on changes in design, dimensions, material selection, hardware, client requirements or site conditions.
• No Responsibility for Non-Tatsavi Work: Any work executed, modified, repaired, altered or installed by the client, the client's appointed vendor, contractor, carpenter, electrician, plumber or any other third party shall be outside Tatsavi Spaces' responsibility and warranty, even if such work is carried out at the same project site.`,

  paymentDefaultTermination: `The final payment of 5% shall become due and payable on the date of the joint final inspection and handover, subject to completion of the agreed scope of work and inspection of the project.

If any minor, non-material work, adjustment, alignment or touch-up is identified during the final inspection, such item shall not constitute a reason for the Client to withhold, defer, deduct or delay the final payment.

Tatsavi Spaces shall complete such minor pending items within 48 hours of the final inspection, subject to site access and availability of any required material or hardware.

For the avoidance of doubt, minor pending works that do not materially affect the functionality, safety or intended use of the completed work shall not affect the payment due date, and the Client shall remain liable to make the full final payment on the date of the joint final inspection and handover.

If the Client fails to make any payment due under this Agreement within 15 days from the applicable payment due date, such failure shall constitute a material breach of this Agreement, provided Tatsavi Spaces has completed the agreed scope, applicable rectifications and agreed handover requirements.

Tatsavi Spaces may issue a written notice requiring the Client to clear the outstanding amount within the period specified in such notice. If the outstanding amount is not received within the specified notice period, Tatsavi Spaces shall have the right to terminate the Agreement, without prejudice to its right to recover all outstanding amounts, applicable charges, costs and other amounts legally recoverable under this Agreement or applicable law.

Upon termination due to payment default:
1. All outstanding amounts payable to Tatsavi Spaces shall become immediately due and payable.
2. Tatsavi Spaces shall have no obligation to provide further work, service, rectification, maintenance or handover-related assistance until all outstanding dues are cleared, except where otherwise required by applicable law.
3. Reasonable costs, losses or expenses incurred by Tatsavi Spaces as a direct result of the Client's payment default may be recovered from the Client, to the extent permitted by applicable law.
4. Termination shall not cancel, reduce, waive or extinguish any payment obligation or liability that accrued before termination.
5. Any applicable warranty or post-handover support shall remain subject to the terms of this Agreement and clearance of all outstanding dues, to the extent permitted by applicable law.`,
};

export const AGREEMENT_SECTIONS = [
  { key: "paymentTerms", defaultNumber: 2, title: "Payment Terms", rows: 10, hint: "Payment schedule milestones, inspection & final payment terms" },
  { key: "scopeChanges", defaultNumber: 3, title: "Changes / Additions to Scope", rows: 4, hint: "Extra work and approval policy" },
  { key: "projectTimeline", defaultNumber: 4, title: "Project Timeline", rows: 6, hint: "Days for completion, society & third party restrictions" },
  { key: "siteAccess", defaultNumber: 5, title: "Site Access & Communication", rows: 5, hint: "Site access rules, society restrictions & 3rd party indemnity" },
  { key: "materialSelection", defaultNumber: 6, title: "Material Selection & Quality", rows: 3, hint: "Vendor selection & quality commitments" },
  { key: "cancellationRefunds", defaultNumber: 7, title: "Cancellation & Refunds", rows: 2, hint: "Cancellation and refund terms" },
  { key: "handoverCleaning", defaultNumber: 8, title: "Handover & Final Cleaning", rows: 3, hint: "Final payment receipt & 48-hour cleaning window" },
  { key: "disputeResolution", defaultNumber: 9, title: "Dispute Resolution", rows: 3, hint: "Amicable settlement & jurisdiction location" },
  { key: "declaration", defaultNumber: 10, title: "Declaration", rows: 3, hint: "Agreement acceptance declaration clause" },
  { key: "warrantySupport", defaultNumber: 11, title: "Warranty & After-Sales Support", rows: 14, hint: "Ply/hardware warranty, limitations, exclusions & coverage" },
  { key: "termsConditions", defaultNumber: 12, title: "Terms & Conditions", rows: 8, hint: "General terms, exclusions & tolerances" },
  { key: "paymentDefaultTermination", defaultNumber: 13, title: "Payment Default, Final Payment and Agreement Termination", rows: 10, hint: "Final payment, default notices and termination conditions" },
];

const splitAdditionalTerms = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*(?:[-*]|\d+[.)])\s*/, "").trim())
    .filter(Boolean);

const renderSectionContent = (doc, bgImage, text, y) => {
  if (!text || !text.trim()) return y;

  const rawLines = String(text).split(/\r?\n/);
  for (let i = 0; i < rawLines.length; i++) {
    const origLine = rawLines[i];
    const rawLine = origLine.trim();
    if (!rawLine) {
      y += 1.5;
      continue;
    }

    // Check for Subheading: e.g. [Warranty Coverage] or [1. 30-Day Complimentary Support] or ### Warranty Coverage or ## Warranty Coverage
    const subheadingMatch = rawLine.match(/^\[(.*)\]$/) || rawLine.match(/^#{2,3}\s+(.*)$/);
    if (subheadingMatch) {
      y = drawSubheading(doc, bgImage, subheadingMatch[1].trim(), y);
      continue;
    }

    // Check for Website Link line: e.g. For more details you can visit www.tatsavispaces.com/...
    if (/www\.tatsavispaces\.com/i.test(rawLine)) {
      const parts = rawLine.split(/(www\.tatsavispaces\.com\S*)/i);
      const runs = parts.map((part) => {
        if (/^www\./i.test(part)) {
          return { text: part, color: LINK_BLUE };
        }
        return { text: part, color: MUTED_TEXT };
      });
      y = drawRunsLine(doc, bgImage, runs, MARGIN_X, y + 1.5);
      y += 1.5;
      continue;
    }

    // Check for bold header line ending with colon, e.g. "Payment Schedule:" or "Upon termination due to payment default:"
    if (
      /^[A-Za-z0-9\s/&–—,.-]+:\s*$/.test(rawLine) &&
      !rawLine.startsWith("•") &&
      !rawLine.startsWith("-") &&
      !rawLine.startsWith("*")
    ) {
      y += 1.5;
      y = drawRunsLine(doc, bgImage, [{ text: rawLine, bold: true, color: BRAND_PLUM }], MARGIN_X, y);
      y += 1;
      continue;
    }

    // Check for Numbered item, e.g. "1. All outstanding amounts payable..."
    const numMatch = rawLine.match(/^(\d+)[.)]\s+(.*)$/);
    if (numMatch) {
      const numStr = numMatch[1];
      const numContent = numMatch[2].trim();
      y = drawNumberedItem(doc, bgImage, numStr, numContent, y);
      continue;
    }

    // Check for Bullet line: starts with •, -, *
    const bulletMatch = rawLine.match(/^(?:[•\-*])\s*(.*)$/);
    if (bulletMatch) {
      const bulletContent = bulletMatch[1].trim();
      const isIndented = /^\s{2,}/.test(origLine);

      // Check if bullet itself is a bold header, e.g. "• Final 5% Payment, Inspection & Handover" or "• Final 5% Payment, Inspection & Handover:"
      if (/^Final 5% Payment, Inspection & Handover:?$/i.test(bulletContent)) {
        y = drawBullets(doc, bgImage, [bulletContent], y, { bold: true, indent: isIndented ? 5 : 0 });
        continue;
      }

      // Check if bullet content has a bold label, e.g. "10% – Design Initiation: Payable..."
      // or "Quotation Exclusions: Quotation does not..."
      // or "Bulging of Core Material – Any unnatural..."
      const colonMatch = bulletContent.match(/^([^:]+):(?!\/)(.*)$/);
      const labelMatch =
        colonMatch && colonMatch[1].trim().length < 50 && colonMatch[2].trim()
          ? colonMatch
          : bulletContent.match(/^([^:–—]+)(?::| – | — )(.*)$/);
      if (labelMatch && labelMatch[1].trim().length < 50 && labelMatch[2].trim()) {
        const label = labelMatch[1].trim();
        const content = labelMatch[2].trim();
        y = drawLabeledBullets(doc, bgImage, [{ label, text: content }], y, { indent: isIndented ? 5 : 0 });
      } else {
        y = drawBullets(doc, bgImage, [bulletContent], y, { indent: isIndented ? 5 : 0 });
      }
      continue;
    }

    // Otherwise, standard paragraph
    y = drawParagraph(doc, bgImage, rawLine, y);
    y += 1.0;
  }

  return y;
};

const addAgreementSections = async (
  doc,
  bgImage,
  agreementDate,
  clientName,
  signatureImage,
  additionalTerms = "",
  startY = 30,
  clientEmail = "",
  clientPhone = "",
  agreementTerms = {}
) => {
  let y = startY;
  let sectionNumber = 2;

  // Render configured sections (2 to 13)
  for (let i = 0; i < AGREEMENT_SECTIONS.length; i++) {
    const section = AGREEMENT_SECTIONS[i];
    const rawVal = agreementTerms[section.key] !== undefined
      ? agreementTerms[section.key]
      : (section.key === "handoverCleaning" && agreementTerms.handoverApproval !== undefined
          ? agreementTerms.handoverApproval
          : DEFAULT_AGREEMENT_TERMS[section.key]);

    if (rawVal && rawVal.trim()) {
      if (i > 0 && y > 30) {
        y = ensureRoom(doc, bgImage, y + 2, 24);
        y = drawDivider(doc, bgImage, y);
      }
      y = drawSectionTitle(doc, bgImage, sectionNumber++, section.title, y);
      y = renderSectionContent(doc, bgImage, rawVal, y);
    }
  }

  // Additional Terms
  const addlText = String(agreementTerms.additionalTerms !== undefined ? agreementTerms.additionalTerms : additionalTerms || "").trim();
  if (addlText) {
    if (y > 30) {
      y = ensureRoom(doc, bgImage, y + 2, 24);
      y = drawDivider(doc, bgImage, y);
    }
    y = drawSectionTitle(doc, bgImage, sectionNumber++, "Additional Terms and Conditions", y);
    y = renderSectionContent(doc, bgImage, addlText, y);
  }

  // Draw Signatures section (keep whole box together)
  const hasExtraContact = !!(clientPhone || clientEmail);
  const boxH = hasExtraContact ? 44 : 36;
  y = ensureRoom(doc, bgImage, y + 6, boxH + 16);
  if (y > 30) {
    y = drawDivider(doc, bgImage, y);
  }
  y = drawSectionTitle(doc, bgImage, sectionNumber++, "Signatures", y);

  const colWidth = (CONTENT_WIDTH - 12) / 2;
  const leftX = MARGIN_X;
  const rightX = MARGIN_X + colWidth + 12;

  // Box background for signatures
  doc.setFillColor(250, 250, 252);
  doc.setDrawColor(...LINE_GREY);
  doc.setLineWidth(0.3);
  doc.roundedRect(leftX, y, colWidth, boxH, 2, 2, "FD");
  doc.roundedRect(rightX, y, colWidth, boxH, 2, 2, "FD");

  // Client Side
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(...BRAND_PLUM);
  doc.text("Client Acceptance", leftX + 5, y + 6.5);
  doc.text("Client Acceptance", leftX + 5.16, y + 6.5);
  doc.text("Client Acceptance", leftX + 5.08, y + 6.56);
  doc.text("Client Acceptance", leftX + 5.08, y + 6.44);
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...BODY_TEXT);

  let currentClientY = y + 7.5;
  if (clientPhone) {
    currentClientY += 4.5;
    doc.text(`Phone: ${clientPhone}`, leftX + 5, currentClientY);
  }
  if (clientEmail) {
    currentClientY += 4.5;
    doc.text(`Email: ${clientEmail}`, leftX + 5, currentClientY);
  }

  const lineY = Math.max(y + 25, currentClientY + 4);
  doc.setDrawColor(...LINE_GREY);
  doc.line(leftX + 5, lineY, leftX + colWidth - 5, lineY);
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED_TEXT);
  doc.text("Signature", leftX + 5, lineY + 3.5);
  doc.text(`Date: ${agreementDate}`, leftX + 5, lineY + 7.5);

  // Company Side
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(...BRAND_PLUM);
  doc.text("Authorized Signatory", rightX + 5, y + 6.5);
  doc.text("Authorized Signatory", rightX + 5.16, y + 6.5);
  doc.text("Authorized Signatory", rightX + 5.08, y + 6.56);
  doc.text("Authorized Signatory", rightX + 5.08, y + 6.44);
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...BODY_TEXT);
  doc.text("Tatsavi Spaces: Shashikant Kadukar", rightX + 5, y + 12);

  // Signature image
  if (signatureImage) {
    const sigDims = await getImageDimensions(signatureImage);
    const { width: sigW, height: sigH } = fitImageBoxMm(sigDims.width, sigDims.height, 28, 9.5);
    doc.addImage(signatureImage, imageFormatFromDataUrl(signatureImage), rightX + 5, lineY - sigH - 1, sigW, sigH);
  }
  doc.setDrawColor(...LINE_GREY);
  doc.line(rightX + 5, lineY, rightX + colWidth - 5, lineY);
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED_TEXT);
  doc.text("Authorized Stamp / Signature", rightX + 5, lineY + 3.5);
  doc.text(`Date: ${agreementDate}`, rightX + 5, lineY + 7.5);

  return y + boxH + 8;
};

export const generateAgreementPDFBlob = async ({
  quote,
  project,
  agreementDate = new Date(),
  agreementClientName,
  agreementAddress,
  agreementClientEmail,
  agreementClientPhone,
  additionalTerms = "",
  agreementTerms = {},
  quotePdfUrl = "",
}) => {
  const doc = new jsPDF("p", "mm", "a4");
  const [bgImage, rawLogoImage, rawSignatureImage, montserratRegular, montserratBold] = await Promise.all([
    loadImageAsBase64("/pdf-bg.jpg"),
    loadImageAsBase64("/agreement-logo.png", "png"),
    loadImageAsBase64("/signature-logo.png", "png"),
    loadFontAsBase64(ASSETS.montserratRegular),
    loadFontAsBase64(ASSETS.montserratBold),
  ]);
  const logoImage = await trimImageDataUrl(rawLogoImage);
  const signatureImage = await trimImageDataUrl(rawSignatureImage);
  registerFonts(doc, montserratRegular, montserratBold);

  const dateText = formatAgreementDate(agreementDate);
  const clientName = agreementClientName || getClientName(project, quote);
  const clientEmail = agreementClientEmail !== undefined ? agreementClientEmail : getClientEmail(project, quote);
  const clientPhone = agreementClientPhone !== undefined ? agreementClientPhone : getClientPhone(project, quote);
  const projectAddress = agreementAddress || getProjectAddress(project);
  const activeItems = activeQuoteItems(quote?.items || []);
  const totals = getQuoteCommercialTotals(quote, activeItems);

  addBackground(doc, bgImage);
  const titleY = await drawCenteredTopLogo(doc, logoImage);

  doc.setFont("Montserrat", "bold");
  doc.setFontSize(17.5);
  doc.setTextColor(...BRAND_PLUM);
  const titleText = "CUSTOMER AGREEMENT CONTRACT";
  const titleOffsets = [
    [0, 0], [0.24, 0], [-0.24, 0], [0, 0.22], [0, -0.22],
    [0.17, 0.17], [-0.17, -0.17], [0.17, -0.17], [-0.17, 0.17],
    [0.12, 0], [-0.12, 0], [0, 0.12], [0, -0.12],
    [0.08, 0.08], [-0.08, -0.08],
  ];
  titleOffsets.forEach(([dx, dy]) => {
    doc.text(titleText, A4_WIDTH / 2 + dx, titleY + dy, { align: "center" });
  });

  let y = titleY + 7.5;
  y = drawParagraph(doc, bgImage, `This agreement is made on ${dateText}, between:`, y);
  y += 1.5;
  y = drawRunsLine(
    doc,
    bgImage,
    [
      { text: "Tatsavi Spaces", bold: true, color: BRAND_PLUM },
      {
        text: ", a brand of Microinch Hub Solutions LLP, having its office at A210, KPCT Mall, Fatima Nagar, Wanowari, Pune-13",
        color: BODY_TEXT,
      },
    ],
    MARGIN_X,
    y
  );
  y += 2;
  y = drawRunsLine(doc, bgImage, [{ text: "And", bold: true, color: BRAND_PLUM }], MARGIN_X, y);
  y += 1.5;
  y = drawRunsLine(
    doc,
    bgImage,
    [
      { text: "Client Name: ", bold: true, color: BRAND_PLUM },
      { text: clientName, bold: true, color: BRAND_PLUM },
    ],
    MARGIN_X,
    y
  );
  y += 1.5;
  y = drawRunsLine(
    doc,
    bgImage,
    [
      { text: "Project Name & Address: ", bold: true, color: BRAND_PLUM },
      { text: projectAddress, bold: true, color: BRAND_PLUM },
    ],
    MARGIN_X,
    y
  );
  y = drawDivider(doc, bgImage, y + 4);

  y = drawSectionTitle(doc, bgImage, 1, "Scope of Work", y);
  y = drawParagraph(
    doc,
    bgImage,
    "The Company agrees to provide interior designing services as per the shared quotation and scope. The design execution will follow the specifications, timelines, and materials agreed upon.",
    y
  );

  const tableRows = buildQuoteTableRows(quote);
  if (tableRows.length > 0) {
    y = ensureRoom(doc, bgImage, y + 2, 22);
    autoTable(doc, {
      startY: y,
      margin: { top: 22, bottom: 26, left: MARGIN_X, right: MARGIN_X },
      head: [["Sr No", "Item & Description", "Material / Spec", "Dimension", "Amount (Rs.)"]],
      body: tableRows,
      theme: "grid",
      pageBreak: "auto",
      rowPageBreak: "avoid",
      showHead: "everyPage",
      styles: {
        font: "Montserrat",
        fontSize: 7.8,
        textColor: BODY_TEXT,
        cellPadding: { top: 1.6, bottom: 1.6, left: 2, right: 2 },
        lineColor: LINE_GREY,
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: BRAND_PLUM,
        textColor: [255, 255, 255],
        font: "Montserrat",
        fontStyle: "bold",
        fontSize: 9,
        halign: "center",
        cellPadding: { top: 2.8, bottom: 2.8, left: 2, right: 2 },
      },
      alternateRowStyles: { fillColor: [253, 251, 254] },
      columnStyles: {
        0: { cellWidth: 12, halign: "center" },
        1: { cellWidth: 72, halign: "left" },
        2: { cellWidth: 40, halign: "left" },
        3: { cellWidth: 22, halign: "center" },
        4: { cellWidth: 28, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "head") {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.text = [""]; // Clear default text to draw crisp high-contrast bold white text in didDrawCell
        } else if (data.section === "body" && data.cell.raw?.isSpaceHeader) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.textColor = BRAND_PLUM;
          data.cell.styles.halign = "center";
          data.cell.text = [""]; // Custom drawn in didDrawCell for bold centered rendering
        } else if (data.section === "body" && (data.cell.raw?.isSubtotalLabel || data.cell.raw?.isSubtotalAmount)) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.textColor = BRAND_PLUM;
          data.cell.styles.halign = "right";
          data.cell.styles.fillColor = [248, 244, 252];
          data.cell.text = [""]; // Custom drawn in didDrawCell for bold subtotal rendering
        }
      },
      didDrawCell: (data) => {
        if (data.section === "head") {
          const raw = data.cell.raw || "";
          const text = String(typeof raw === "object" ? raw.content || "" : raw).trim();
          if (!text) return;

          const fontSize = 9;
          const yPos = data.cell.y + data.cell.height / 2 + 1.2;
          let xPos = data.cell.x + data.cell.width / 2;
          let align = "center";

          if (data.cell.styles.halign === "left") {
            xPos = data.cell.x + data.cell.padding("left");
            align = "left";
          } else if (data.cell.styles.halign === "right") {
            xPos = data.cell.x + data.cell.width - data.cell.padding("right");
            align = "right";
          }

          doc.setFont("Montserrat", "bold");
          doc.setFontSize(fontSize);
          doc.setTextColor(255, 255, 255);
          doc.text(text, xPos, yPos, { align });
          doc.text(text, xPos + 0.18, yPos, { align });
          doc.text(text, xPos, yPos - 0.08, { align });
          doc.text(text, xPos + 0.09, yPos + 0.08, { align });
        } else if (data.section === "body" && data.cell.raw?.isSpaceHeader) {
          const raw = data.cell.raw || {};
          const text = String(raw.content || "").trim();
          if (!text) return;

          const fontSize = 9;
          const yPos = data.cell.y + data.cell.height / 2 + 1.2;
          const xPos = data.cell.x + data.cell.width / 2;

          doc.setFont("Montserrat", "bold");
          doc.setFontSize(fontSize);
          doc.setTextColor(...BRAND_PLUM);
          doc.text(text, xPos, yPos, { align: "center" });
          doc.text(text, xPos + 0.18, yPos, { align: "center" });
          doc.text(text, xPos, yPos - 0.08, { align: "center" });
          doc.text(text, xPos + 0.09, yPos + 0.08, { align: "center" });
        } else if (data.section === "body" && (data.cell.raw?.isSubtotalLabel || data.cell.raw?.isSubtotalAmount)) {
          const raw = data.cell.raw || {};
          const text = String(raw.content || "").trim();
          if (!text) return;

          const fontSize = 8.8;
          const yPos = data.cell.y + data.cell.height / 2 + 1.1;
          const xPos = data.cell.x + data.cell.width - data.cell.padding("right");

          doc.setFont("Montserrat", "bold");
          doc.setFontSize(fontSize);
          doc.setTextColor(...BRAND_PLUM);
          doc.text(text, xPos, yPos, { align: "right" });
          doc.text(text, xPos + 0.18, yPos, { align: "right" });
          doc.text(text, xPos, yPos - 0.08, { align: "right" });
          doc.text(text, xPos + 0.09, yPos + 0.08, { align: "right" });
        }
      },
      willDrawPage: ({ pageNumber }) => {
        if (pageNumber > 1) {
          addBackground(doc, bgImage);
        }
      },
    });

    y = (doc.lastAutoTable?.finalY || y) + 5;
  }

  if (totals.total > 0) {
    y = drawRunsLine(
      doc,
      bgImage,
      [
        { text: "Total Quotation Amount: ", bold: true, color: BRAND_PLUM },
        { text: `Rs. ${formatInr(totals.total)}/-`, bold: true, color: BRAND_PLUM },
      ],
      MARGIN_X,
      y + 1
    );
  }
  if (quotePdfUrl) {
    y = drawQuoteLink(doc, bgImage, quotePdfUrl, y + 1);
  }
  y = drawDivider(doc, bgImage, y + 4);

  // Continue to subsequent sections cleanly passing the current `y` coordinate
  await addAgreementSections(
    doc,
    bgImage,
    dateText,
    clientName,
    signatureImage,
    additionalTerms,
    y,
    clientEmail,
    clientPhone,
    agreementTerms
  );

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    addFooter(doc, page, totalPages);
  }

  return new Blob([doc.output("arraybuffer")], { type: "application/pdf" });
};

export const generateAgreementPDF = async ({
  quote,
  project,
  agreementDate = new Date(),
  agreementClientName,
  agreementAddress,
  agreementClientEmail,
  agreementClientPhone,
  additionalTerms = "",
  agreementTerms = {},
  quotePdfUrl = "",
}) => {
  const blob = await generateAgreementPDFBlob({
    quote,
    project,
    agreementDate,
    agreementClientName,
    agreementAddress,
    agreementClientEmail,
    agreementClientPhone,
    additionalTerms,
    agreementTerms,
    quotePdfUrl,
  });
  const url = URL.createObjectURL(blob);
  const clientName = (agreementClientName || getClientName(project, quote)).replace(/[\\/:*?"<>|]/g, "-");
  const a = document.createElement("a");
  a.href = url;
  a.download = `Agreement_${clientName}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};


